import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../../services/api';
import { messaggioDa } from '../../utils/notifiche';

/**
 * NonFatturate — le prestazioni eseguite da più di N giorni e non ancora
 * in una fattura emessa (Alex, 28 settembre 2026). Sta in testa a
 * Fatturazione, dove la segreteria lavora, e compare solo se c'è qualcosa.
 *
 * Perché serve: la spettanza del medico matura alla fattura emessa, quindi
 * una prestazione mai fatturata non paga il medico e non incassa. Cosa è
 * «non fatturata» lo decide il database (migration 268), lo stesso conto del
 * riepilogo del mattino: qui si mostra e basta. «Apri la cassa» porta alla
 * Cassa del paziente, dove la prestazione si fattura come sempre.
 */
const euro = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const dataIta = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
    return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
};

const NonFatturate = () => {
    const navigate = useNavigate();
    const [stato, setStato] = useState(null);
    const [aperto, setAperto] = useState(false);

    useEffect(() => {
        api.get('/fatturazione/non-fatturate')
            .then((r) => setStato({ ...r.data.data, messaggio: r.data.message || null }))
            .catch((e) => setStato({ errore: messaggioDa(e, 'Non riesco a calcolare le prestazioni non fatturate') }));
    }, []);

    if (!stato) return null;
    if (stato.errore || stato.giorni === null) {
        return (
            <div className="fat-nf fat-nf--errore" role="status">
                <AlertTriangle size={16} aria-hidden="true" />
                <span>{stato.errore || stato.messaggio}</span>
            </div>
        );
    }
    const n = stato.prestazioni.length;
    if (!n) return null;
    const totale = stato.prestazioni.reduce((s, x) => s + Number(x.importo || 0), 0);

    return (
        <section className="fat-nf" aria-label="Prestazioni non fatturate">
            <button type="button" className="fat-nf-testa" onClick={() => setAperto((a) => !a)} aria-expanded={aperto}>
                <AlertTriangle size={16} aria-hidden="true" />
                <span>
                    <strong>{n === 1 ? '1 prestazione eseguita' : `${n} prestazioni eseguite`}</strong>
                    {` da più di ${stato.giorni} giorni e non fatturate`} ({euro(totale)})
                </span>
                {aperto ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
            </button>
            {aperto && (
                <div className="rm-scorre">
                    <table className="fat-nf-tabella">
                        <thead>
                            <tr>
                                <th>Data</th><th>Paziente</th><th>Prestazione</th><th>Medico</th>
                                <th className="text-right">Importo</th><th className="text-right">Giorni</th><th />
                            </tr>
                        </thead>
                        <tbody>
                            {stato.prestazioni.map((x) => (
                                <tr key={x.id}>
                                    <td className="rm-intero">{dataIta(x.data_visita)}</td>
                                    <td>{[x.cognome, x.nome].filter(Boolean).join(' ')}</td>
                                    <td>
                                        <span className="rm-tronca" title={x.prestazione}>{x.prestazione}</span>
                                        {x.in_bozza && <span className="fat-nf-segno">in una bozza</span>}
                                        {x.azienda && <span className="fat-nf-segno">medicina del lavoro</span>}
                                    </td>
                                    <td>{[x.medico_titolo, x.medico_cognome].filter(Boolean).join(' ') || '—'}</td>
                                    <td className="text-right">{euro(x.importo)}</td>
                                    <td className="text-right">{x.giorni}</td>
                                    <td>
                                        <button type="button" className="fat-btn"
                                                onClick={() => navigate(`/pazienti/${x.paziente_id}?vista=incassi`)}>
                                            Apri la cassa
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};

export default NonFatturate;
